import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useLanguage } from "@/contexts/LanguageContext";

interface AddTeamFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

export const AddTeamForm = ({ isOpen, onClose, onSuccess }: AddTeamFormProps) => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Server-side trigger will enforce limits
      const { error } = await supabase
        .from("teams")
        .insert([formData]);

      if (error) {
        // Check if it's a limit error
        if (error.message?.includes('Team limit reached')) {
          toast({
            title: "Team Limit Reached",
            description: error.message,
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
        throw error;
      }

      toast({
        title: "Team Created",
        description: "New team has been created successfully",
      });

      await onSuccess();
      onClose();
      setFormData({ name: "", description: "", status: "active" });
    } catch (error: any) {
      console.error("Error creating team:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add New Team</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          <div className="space-y-2">
            <Label htmlFor="name">Team Name *</Label>
            <Input
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter team name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter team description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="form-buttons">
            <Button variant="outline" type="button" onClick={onClose} className="form-button" disabled={isSubmitting}>
              {t('cancel')}
            </Button>
            <Button type="submit" className="form-button" disabled={isSubmitting}>
              {isSubmitting ? t('creating') : t('createTeam')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
